import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, MapPin, Menu, Package, ShieldCheck, UserRound, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { label: 'Profile', href: '/profile', icon: UserRound },
  { label: 'Orders', href: '/orders', icon: Package },
];

const UserPanelShell = ({ title, subtitle, children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const SidebarContent = () => (
    <>
      <div className="flex items-center justify-between border-b border-[#3e2f20] pb-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-14 w-14 bg-[#a97752] grid place-items-center text-xl font-semibold uppercase">
            {(user?.name || 'U').slice(0, 1)}
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate">{user?.name || 'Customer'}</p>
            <p className="text-sm text-[#b39b81] truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={() => setMenuOpen(false)} className="lg:hidden p-2 text-[#d8c3aa]" aria-label="Close account menu">
          <X size={20} />
        </button>
      </div>

      <nav className="mt-5 space-y-2">
        {navItems.map((item) => {
          const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-3 px-3 py-3 text-sm font-medium transition ${active ? 'bg-[#3d291d] text-[#f6e8d6]' : 'text-[#d8c3aa] hover:bg-[#221a14] hover:text-[#f8efe5]'}`}
            >
              <item.icon size={18} />
              {item.label}
            </Link>
          );
        })}
        <Link to="/stores" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-[#d8c3aa] hover:bg-[#221a14] hover:text-[#f8efe5]">
          <MapPin size={18} />
          Stores
        </Link>
        {isAdmin && (
          <Link to="/admin/dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-[#d8c3aa] hover:bg-[#221a14] hover:text-[#f8efe5]">
            <ShieldCheck size={18} />
            Admin Panel
          </Link>
        )}
        <button onClick={logout} className="flex w-full items-center gap-3 px-3 py-3 text-sm font-medium text-red-200 hover:bg-red-950/40">
          <LogOut size={18} />
          Logout
        </button>
      </nav>
    </>
  );

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button onClick={() => setMenuOpen(true)} className="lg:hidden mb-5 inline-flex items-center gap-2 bg-[#1F2A44] text-[#FAF7F2] px-4 py-2 text-sm font-semibold" aria-label="Open account menu">
          <Menu size={18} />
          Account Menu
        </button>
        {menuOpen && <button className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMenuOpen(false)} aria-label="Close account menu overlay" />}
        <aside className={`fixed inset-y-0 left-0 z-50 w-[285px] bg-[#0c0907] text-[#f4e9db] border-r border-[#3e2f20] p-5 transition-transform lg:hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <SidebarContent />
        </aside>
        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          <aside className="hidden lg:block bg-[#0c0907] text-[#f4e9db] border border-[#3e2f20] p-5 h-fit lg:sticky lg:top-28">
            <SidebarContent />
          </aside>

          <section>
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.18em] text-[#8b5e34] font-semibold">Account Dashboard</p>
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#1F2A44] mt-1">{title}</h1>
              {subtitle && <p className="text-sm text-[#1F2A44]/70 mt-2">{subtitle}</p>}
            </div>
            {children}
          </section>
        </div>
      </div>
    </main>
  );
};

export default UserPanelShell;
